/**
 * Dedicated Enterprise Landing Page (/enterprise).
 *
 * Archis as the governed way an organisation rolls out Claude Code and Cursor on its
 * repositories (#1170), for CISOs, VPs of Engineering, IT and Procurement:
 * 1. Hero: an index of the company's repositories for every developer's agent, Archis
 *    seats from the identity provider and Archis's own admin events in the SIEM, and the
 *    "one workspace" block. It makes the same claim as the trust boundary below.
 * 2. Trust boundary: interactive local vs. cloud data flow, and what never reaches Archis
 * 3. Pillars, each linked to its documentation: Identity; your repositories as one
 *    system on your own server; Continuity; Observability; Procurement & Support
 * 4. Fleet ROI calculator
 * 5. Pilot and contact form (direct useSubmitEnterpriseLead)
 * 6. Due diligence FAQ
 *
 * Every pillar names a shipped capability. The local read policy (#1164) and the
 * local audit forwarder (#1165) join the page when they ship, not before; the
 * retired claims are listed in scripts/check-claims.mjs.
 */

import { useState, useRef } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { CALCULATOR } from '@/entities/metrics'
import { useSubmitEnterpriseLead } from '@/shared/api/hooks'
import { capture } from '@/shared/lib/analytics'
import { Footer, Masthead } from '@/shared/ui/Chrome'
import { CheckIcon } from '@/shared/ui/icons'
import {
  Badge,
  Button,
  ErrorNotice,
  Field,
  Label,
} from '@/shared/ui/primitives'
import { Turnstile } from '@/shared/ui/Turnstile'
import { PrivacyBoundary } from '@/shared/ui/walkthrough'

interface Pillar {
  index: string
  label: string
  badge: string
  title: string
  lead: string
  items: { name: string; text: ReactNode }[]
  docs?: { to: string; label: string }
  /** Spans both columns: the odd one out in a two-column grid. */
  wide?: boolean
}

/** The pillars, in the order a buyer's committee asks about them. */
const PILLARS: Pillar[] = [
  {
    index: 'P-01',
    label: 'Identity & Lifecycle',
    badge: 'Automated',
    title: 'SAML SSO, SCIM and Domain Capture',
    lead: 'Sign-in follows your SAML or OIDC identity provider, and seats follow Okta or Microsoft Entra ID over SCIM.',
    items: [
      {
        name: 'Single sign-on',
        text: 'SAML or OIDC through your identity provider, and it can be required: a session signed in any other way is refused.',
      },
      {
        name: 'SCIM 2.0 provisioning',
        text: 'Tested with Okta and Microsoft Entra ID. Deactivating someone releases their seat and revokes their devices and sign-in tokens. A laptop that stays offline keeps its current lease until it runs out: at most four days, less if you shorten the offline grace. A sign-in to your CAVS DB Server lasts seven days and Archis cannot revoke it, so a removed member keeps reading what the server holds until it expires, unless you run cavsdb-server user block.',
      },
      {
        name: 'Domain capture',
        text: 'Prove your domain with a DNS record, and anyone signing in under it joins your workspace instead of starting their own.',
      },
      {
        name: 'Devices and machine tokens',
        text: 'Revoke a lost laptop from the portal and restore it later. CI runners, Codespaces and containers get their own expiring tokens.',
      },
    ],
    docs: { to: '/docs/security#workspace', label: 'Sign-in and offboarding' },
  },
  {
    index: 'P-02',
    label: 'Federation & Server',
    badge: 'Self-hosted',
    title: 'Your Repositories as One System, on Your Server',
    lead: 'Register your repositories once and work across them as one, on a CAVS DB Server you run on your own infrastructure.',
    items: [
      {
        name: 'Federation',
        text: (
          <>
            A workspace pins every repository to an exact state, and a lock file reproduces that state on another machine. A feature is followed across repositories through squash merges and rebases, and <code className="code">chi repo impact</code> lists who consumes a repository.
          </>
        ),
      },
      {
        name: 'Self-hosted CAVS DB Server',
        text: 'Download it for Linux or Windows and generate its licence in the portal. It checks the licence without a network connection, members sign in with their Archis account, and the rules for who reaches what stay on the server.',
      },
      {
        name: 'Releases, environments and incidents',
        text: (
          <>
            A release is a locked composition; an environment can require approvals before it takes one. <code className="code">chi env diff</code> compares two environments across every repository, and <code className="code">chi env replay</code> shows what was live at the moment of an incident and what changed since the deployment before.
          </>
        ),
      },
      {
        name: 'Retention holds and compliance exports',
        text: (
          <>
            A hold keeps a state from being collected until it is lifted. <code className="code">chi registry export</code> writes a deterministic compliance export to a file, and <code className="code">chi registry verify-export</code> checks it.
          </>
        ),
      },
      {
        name: 'What the server holds',
        text: (
          <>
            Each repository&rsquo;s ledger (paths, hashes and commits) and the compositions built on it. How much source goes with it is your choice per repository: in <code className="code">metadata</code> mode a member with no checkout lists paths and never reads bytes, reported as <code className="code">without_content</code>; <code className="code">hybrid</code>, the default, and <code className="code">full</code> publish file content so it can be searched without a checkout. None of it reaches Archis.
          </>
        ),
      },
    ],
    docs: { to: '/docs/cli#federation', label: 'Federation and server commands' },
  },
  {
    index: 'P-03',
    label: 'Continuity',
    badge: 'Offline',
    title: 'A Licence That Keeps Working',
    lead: 'What keeps working when the network, the server licence or the subscription goes away.',
    items: [
      {
        name: 'Offline',
        text: 'A signed lease keeps licensed commands working for up to four days without a connection: valid for 24 hours, then a 72-hour grace.',
      },
      {
        name: 'Air-gapped',
        text: 'Enterprise issues an offline licence file, signed for the machine and valid for a set period. It cannot be revoked, so it runs until it expires.',
      },
      {
        name: 'Server read-only, never deleted',
        text: 'An expired or revoked server licence makes the CAVS DB Server read-only: reads, compositions and locks keep working, publishing stops, and nothing is deleted.',
      },
      {
        name: 'When a subscription ends',
        text: (
          <>
            Local indexes stay on every machine and the account drops to Free. The commands that clean up federation (<code className="code">chi repo remove</code>, <code className="code">chi workspace gc</code>, <code className="code">evict</code> and <code className="code">lift</code>, <code className="code">chi registry tenant --clear</code>) still run without a licence.
          </>
        ),
      },
    ],
    docs: { to: '/docs/security#server-licence', label: 'What happens when a licence lapses' },
  },
  {
    index: 'P-04',
    label: 'Observability & SecOps',
    badge: 'Compliance',
    title: 'SIEM Streaming & Audit Logging',
    lead: 'Every administrative action, streamed to your SIEM.',
    items: [
      {
        name: 'SIEM streaming',
        text: 'Each audit event is posted as JSON to Splunk HEC, Datadog, or any collector that accepts it over HTTPS.',
      },
      {
        name: 'Append-only audit log',
        text: 'Seats, members, devices, SSO, SCIM, domain and server-licence changes, with who made them and CSV export.',
      },
      {
        name: 'What your agents read stays on the machine',
        text: 'Query history is kept on each developer’s machine and never sent to Archis. Archis cannot tell you what your agents read, so nobody can get it from Archis either.',
      },
      {
        name: 'Usage dashboard',
        text: 'Organization-wide counts from developers who opted in to telemetry. Every figure is a floor.',
      },
    ],
    docs: { to: '/verify#network', label: 'Every request the CLI makes' },
  },
  {
    index: 'P-05',
    label: 'Procurement & Support',
    badge: 'Direct Support',
    title: 'Enterprise Agreements & Direct Support',
    lead: 'Direct onboarding from the people who build the engine, and commercial terms your procurement team expects.',
    items: [
      {
        name: 'Founder-led architecture review',
        text: 'Technical consultation and deployment guidance with the core team.',
      },
      {
        name: 'Direct engineering support',
        // claims-ok(sla-msa): says that no SLA exists
        text: 'Answered by the engineers who build Archis. No response-time SLA is offered yet.',
      },
      {
        name: 'DPA on request',
        text: 'Our data processing agreement is in counsel review; ask for the current draft.',
      },
      {
        name: 'Annual invoicing and PO terms',
        text: 'Corporate procurement via ACH, wire transfer, or Net 30 terms.',
      },
    ],
    wide: true,
  },
]

export function Enterprise() {
  // Lead Form State
  const [inquiryName, setInquiryName] = useState('')
  const [inquiryEmail, setInquiryEmail] = useState('')
  const [inquiryCompany, setInquiryCompany] = useState('')
  const [inquiryTeamSize, setInquiryTeamSize] = useState('')
  const [inquiryIdp, setInquiryIdp] = useState('')
  const [inquiryNotes, setInquiryNotes] = useState('')
  const [cfToken, setCfToken] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [enterpriseSubmitted, setEnterpriseSubmitted] = useState(false)

  // Enterprise Fleet ROI Calculator State
  const [calcSeats, setCalcSeats] = useState(100)
  const [calcSpendPerDev, setCalcSpendPerDev] = useState(300)

  const enterpriseLead = useSubmitEnterpriseLead()
  const contactFormRef = useRef<HTMLDivElement | null>(null)

  function scrollToContact() {
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Which of the page's CTAs a buyer used, so the lead form's submissions can be
  // read against the intent that led to them.
  function trackCta(cta: string, location: string, seats?: number) {
    capture('enterprise_cta_clicked', { cta, location, seats })
  }

  function contactFrom(location: string, seats?: number) {
    trackCta('contact', location, seats)
    scrollToContact()
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    const fullNotes = [
      inquiryIdp ? `Identity Provider: ${inquiryIdp}` : '',
      inquiryNotes ? `Notes: ${inquiryNotes}` : '',
    ]
      .filter(Boolean)
      .join(' | ')

    enterpriseLead.mutate(
      {
        name: inquiryName,
        email: inquiryEmail,
        company: inquiryCompany,
        teamSize: inquiryTeamSize,
        notes: fullNotes,
        cfToken,
        website: honeypot,
      },
      {
        onSuccess: () => setEnterpriseSubmitted(true),
      },
    )
  }

  // ROI calculations for enterprise fleets
  // An estimate of the Enterprise seat, the floor the pricing research set beside
  // Team's $8 (#922). A contract sets the real figure.
  const seatMonthlyEst = 15
  const totalMonthlySeatCost = calcSeats * seatMonthlyEst
  const totalMonthlyLLMSpend = calcSeats * calcSpendPerDev
  // The measured per-session token saving (CALCULATOR.expected and .high: 22%
  // typical, 51% on solved tasks) on the share of the bill that is repository work. Exposure figures such as
  // the 89–95% task-context band overstate a bill 2–3x, so they are not used here.
  const repositoryShare = totalMonthlyLLMSpend * CALCULATOR.defaultContextShare
  const estimatedMonthlySavings = Math.round(repositoryShare * CALCULATOR.expected)
  const netMonthlyBenefit = estimatedMonthlySavings - totalMonthlySeatCost
  const annualSavings = estimatedMonthlySavings * 12
  const annualSavingsLow = Math.round(repositoryShare * CALCULATOR.low) * 12
  const annualSavingsHigh = Math.round(repositoryShare * CALCULATOR.high) * 12
  const netAnnualBenefit = netMonthlyBenefit * 12
  const savingPerSeatCost = totalMonthlySeatCost > 0
    ? (estimatedMonthlySavings / totalMonthlySeatCost).toFixed(1)
    : '0'

  return (
    <>
      <Masthead />
      <main className="enterprise-page" id="main">
        {/* ────────────────────────────────── 1. Hero Section ────────────── */}
        <section className="shell" style={{ paddingBlock: 'var(--s8) var(--s6)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <div className="row" style={{ gap: 'var(--s3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <Label index="01">Archis for Enterprise</Label>
              <span className="badge badge--signal">Source never reaches Archis</span>
              <span className="badge">SAML 2.0 &amp; SCIM</span>
            </div>

            <h1 style={{ fontSize: 'clamp(2.4rem, 5.4vw, 4rem)', lineHeight: 1.15, maxWidth: '28ch' }}>
              Give every developer&apos;s Claude Code or Cursor an index of your company&apos;s repositories,{' '}
              <span className="accent-text">with Archis seats from your identity provider and every Archis administrative change in your SIEM.</span>
            </h1>

            <p className="lead" style={{ maxWidth: '68ch' }}>
              Your engineers already want coding agents on the company&apos;s code. Archis gives each one an index of
              your repositories on the developer&apos;s machine, Archis seats from your identity provider, and the Archis
              audit log (seats, members, devices, sign-in settings) streamed to your SIEM. Archis does not see or audit
              what the agents do, and never receives a line of source. Your agents still send their
              prompts, and the code they read, to their model provider; Archis cuts how much of it, not where it goes.
            </p>

            <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap', paddingTop: 'var(--s2)' }}>
              <button
                type="button"
                className="cta cta--primary"
                onClick={() => contactFrom('hero')}
              >
                Request a pilot ↓
              </button>
              <Link
                to="/whitepaper"
                className="cta cta--secondary"
                onClick={() => trackCta('whitepaper', 'hero')}
              >
                Read Security &amp; Architecture Whitepaper →
              </Link>
            </div>

            {/* Trust Signals Strip */}
            <div className="row mono-note" style={{ gap: 'var(--s3) var(--s4)', flexWrap: 'wrap', paddingTop: 'var(--s3)', alignItems: 'center' }}>
              <span className="row" style={{ gap: '0.35rem', alignItems: 'center' }}><CheckIcon className="signal" /> Indexing runs on each machine · no endpoint accepts source</span>
              <span className="muted">·</span>
              <span className="row" style={{ gap: '0.35rem', alignItems: 'center' }}><CheckIcon className="signal" /> Okta &amp; Azure AD SAML 2.0 / SCIM 2.0</span>
              <span className="muted">·</span>
              <span className="row" style={{ gap: '0.35rem', alignItems: 'center' }}><CheckIcon className="signal" /> Self-hosted CAVS DB Server</span>
              <span className="muted">·</span>
              <span className="row" style={{ gap: '0.35rem', alignItems: 'center' }}><CheckIcon className="signal" /> Offline and air-gapped licences</span>
              <span className="muted">·</span>
              <span className="row" style={{ gap: '0.35rem', alignItems: 'center' }}><CheckIcon className="signal" /> SIEM streaming (Splunk, Datadog, any HTTPS collector)</span>
            </div>

            {/* Centralize your team */}
            <div className="panel" style={{ padding: 'var(--s5)', marginTop: 'var(--s2)', borderLeft: '3px solid var(--signal)' }}>
              <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 'var(--s3)' }}>
                <div style={{ flex: '1 1 24rem' }}>
                  <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text)' }}>
                    One workspace for every developer&apos;s agent.
                  </div>
                  <p className="dim" style={{ fontSize: '0.875rem', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
                    Developers sign in through your identity provider, and their agents are set up on clone from defaults
                    committed in the repository. SCIM adds and removes seats, the workspace sets how many devices each
                    member may use and how long a machine works offline, and every administrative change lands in the
                    audit log.
                  </p>
                </div>
                <button
                  type="button"
                  className="cta"
                  onClick={() => contactFrom('centralize_callout')}
                  style={{ flexShrink: 0 }}
                >
                  Centralize Your Team →
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ────────────────────────────────── 2. Privacy & Security Boundary ─ */}
        <section className="shell" style={{ paddingBlock: 'var(--s6) var(--s8)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="02">Data Flow &amp; Trust Boundary</Label>
              <h2>What reaches Archis, and what never does.</h2>
              <p className="dim" style={{ maxWidth: '68ch', margin: 0 }}>
                Indexing, the graph and every answer your agent gets from Archis are computed on the developer&apos;s machine. Archis&apos;s servers handle licensing and team administration, and no endpoint accepts source.
                With federation, the registry and the CAVS DB Server are yours as well: repository names, server
                addresses and whatever you publish to the server never reach Archis.
              </p>
            </div>

            {/* Interactive Privacy Boundary Walkthrough Component */}
            <PrivacyBoundary />
            <p className="mono-note" style={{ margin: 0 }}>
              <Link to="/verify#network" className="signal" onClick={() => trackCta('verify', 'boundary')}>
                Every request the CLI makes, and what each one sends →
              </Link>
            </p>

            <div className="grid-2" style={{ gap: 'var(--s4)', marginTop: 'var(--s3)' }}>
              <div className="glass stack" style={{ padding: 'var(--s5)' }}>
                <h4 style={{ margin: 0, fontFamily: 'var(--display)' }}>What never reaches Archis:</h4>
                <ul className="feature-list" style={{ marginTop: 'var(--s2)' }}>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>Raw source code, Git commit history, and diffs</span>
                  </li>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>Local Tree-sitter AST nodes &amp; symbol declaration tables</span>
                  </li>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>Local SQLite call graphs, blast radius maps &amp; test logs</span>
                  </li>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>Developer prompts, MCP tool queries &amp; agent interactions</span>
                  </li>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>Repository names and URLs, and what your CAVS DB Server holds</span>
                  </li>
                </ul>
                <p className="mono-note" style={{ margin: 'var(--s2) 0 0' }}>
                  Your coding agent still sends its prompts, and the code it chooses to read, to its model provider. Archis
                  reduces how much of your repository that is; it does not change where it goes.
                </p>
              </div>

              <div className="glass stack" style={{ padding: 'var(--s5)' }}>
                <h4 style={{ margin: 0, fontFamily: 'var(--display)' }}>What connects to Archis cloud:</h4>
                <ul className="feature-list" style={{ marginTop: 'var(--s2)' }}>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>Cryptographically signed Ed25519 offline seat leases</span>
                  </li>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>SAML 2.0 SSO identity tokens (Okta, Azure AD, Google)</span>
                  </li>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>Optional aggregate telemetry metrics (coarse counts only)</span>
                  </li>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>SIEM audit event forwarder (e.g. seat activation, policy change)</span>
                  </li>
                  <li className="feature-item">
                    <span className="tick" aria-hidden="true" />
                    <span>Your CAVS DB Server&apos;s licence check, when its network allows one</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ────────────────────────────────── 3. The Enterprise Pillars ──── */}
        <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s6)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="03">Enterprise Pillars</Label>
              <h2>What Enterprise adds, and where each part is documented.</h2>
              <p className="dim" style={{ maxWidth: '68ch', margin: 0 }}>
                Identity for IT, your repositories as one system on a server you run, a licence that keeps working
                when the network or the contract does not, and an audit trail for your SOC.
              </p>
            </div>

            <div className="grid-2" style={{ gap: 'var(--s5)' }}>
              {PILLARS.map((pillar) => (
                <div
                  key={pillar.index}
                  className="card stack"
                  style={{ padding: 'var(--s6)', ...(pillar.wide ? { gridColumn: '1 / -1' } : {}) }}
                >
                  <div className="row row--between" style={{ alignItems: 'center' }}>
                    <Label index={pillar.index}>{pillar.label}</Label>
                    <Badge tone="signal">{pillar.badge}</Badge>
                  </div>
                  <h3 style={{ margin: 'var(--s2) 0' }}>{pillar.title}</h3>
                  <p className="dim" style={{ margin: 0, fontSize: '0.9rem' }}>
                    {pillar.lead}
                  </p>
                  <ul className="feature-list" style={{ margin: 'var(--s3) 0' }}>
                    {pillar.items.map((item) => (
                      <li className="feature-item" key={item.name}>
                        <span className="tick" aria-hidden="true" />
                        <span><strong>{item.name}:</strong> {item.text}</span>
                      </li>
                    ))}
                  </ul>
                  {pillar.docs && (
                    <p className="mono-note" style={{ margin: 'auto 0 0' }}>
                      <Link to={pillar.docs.to} className="signal" onClick={() => trackCta('docs', pillar.index)}>
                        {pillar.docs.label} →
                      </Link>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ────────────────────────────────── 4. Fleet ROI Calculator ─────── */}
        <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
          <div className="glass stack" style={{ ['--gap' as string]: 'var(--s6)', padding: 'var(--s6)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <div className="row" style={{ gap: 'var(--s3)', alignItems: 'center' }}>
                <Label index="04">Executive ROI</Label>
                <span className="badge badge--signal">Enterprise Fleet Payback</span>
              </div>
              <h2>Calculate your engineering organization's annual token ROI.</h2>
              <p className="dim" style={{ maxWidth: '68ch', margin: 0 }}>
                Coding agents spend much of each session re-reading files. In our latest benchmark, Archis cut the tokens of a typical Claude Code session by {Math.round(CALCULATOR.expected * 1000) / 10}%, and by {Math.round(CALCULATOR.high * 1000) / 10}% on tasks both runs solved. The calculator applies that to the part of your bill that is repository work.
              </p>
            </div>

            <div className="grid-2" style={{ gap: 'var(--s7)', alignItems: 'center' }}>
              <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
                {/* Fleet Seats Slider */}
                <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                  <div className="row row--between">
                    <span className="readout__caption">Engineering Organization Size</span>
                    <span className="fig" style={{ fontSize: '1.4rem', fontWeight: 600 }}>
                      {calcSeats} engineers
                    </span>
                  </div>
                  <input
                    className="calc__slider"
                    type="range"
                    min={10}
                    max={1000}
                    step={10}
                    value={calcSeats}
                    style={{ ['--fill' as string]: `${((calcSeats - 10) / 990) * 100}%` }}
                    onChange={(e) => setCalcSeats(Number(e.target.value))}
                    aria-label="Engineering organization size"
                  />
                  <div className="row row--between mono-note">
                    <span>10 developers</span>
                    <span>1,000+ developers</span>
                  </div>
                </div>

                {/* Monthly LLM Spend Per Dev */}
                <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                  <div className="row row--between">
                    <span className="readout__caption">Average monthly LLM token spend per developer</span>
                    <span className="fig" style={{ fontSize: '1.4rem', fontWeight: 600 }}>
                      ${calcSpendPerDev} / dev / mo
                    </span>
                  </div>
                  <input
                    className="calc__slider"
                    type="range"
                    min={100}
                    max={800}
                    step={25}
                    value={calcSpendPerDev}
                    style={{ ['--fill' as string]: `${((calcSpendPerDev - 100) / 700) * 100}%` }}
                    onChange={(e) => setCalcSpendPerDev(Number(e.target.value))}
                    aria-label="Monthly spend per developer"
                  />
                  <div className="row row--between mono-note">
                    <span>$100 / mo</span>
                    <span>$800+ / mo</span>
                  </div>
                </div>

                <p className="mono-note" style={{ margin: 0 }}>
                  Assumes {Math.round(CALCULATOR.defaultContextShare * 100)}% of the agent bill is repository work, and
                  that a typical session there uses {Math.round(CALCULATOR.expected * 100)}% fewer tokens (
                  {Math.round(CALCULATOR.high * 100)}% on tasks both arms solved): the {CALCULATOR.basis}. Seat price is
                  an estimate; your contract sets the real one. A pilot measures it on your repositories.{' '}
                  <Link to="/verify" className="signal">How this was measured →</Link>
                </p>
              </div>

              {/* Readout Card */}
              <div className="card stack" style={{ ['--gap' as string]: 'var(--s4)', background: 'var(--panel)', padding: 'var(--s6)' }}>
                <div className="row row--between" style={{ alignItems: 'center' }}>
                  <span className="mono-note">Estimated annual saving, after seats</span>
                  <span className="badge badge--signal">Saves {savingPerSeatCost}× the seat cost</span>
                </div>

                <div className="stack" style={{ ['--gap' as string]: 'var(--s1)' }}>
                  <div
                    className="price"
                    style={{ fontSize: '2.8rem', fontWeight: 600, color: netAnnualBenefit >= 0 ? 'var(--signal)' : 'var(--text-dim)' }}
                  >
                    {netAnnualBenefit >= 0 ? '+' : '−'}${Math.abs(netAnnualBenefit).toLocaleString()}
                    <span className="price__per"> / year</span>
                  </div>
                  <div className="readout__caption">
                    {netAnnualBenefit >= 0 ? 'Net saving after seats' : 'Seats cost more than they save at this spend'}
                  </div>
                </div>

                <div className="grid-2" style={{ gap: 'var(--s3)', borderTop: '1px solid var(--rule)', paddingTop: 'var(--s3)' }}>
                  <div>
                    <div className="fig" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                      ${(totalMonthlySeatCost * 12).toLocaleString()}
                    </div>
                    <div className="readout__caption">Est. seats per year</div>
                  </div>
                  <div>
                    <div className="fig signal" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                      ${annualSavings.toLocaleString()}
                    </div>
                    <div className="readout__caption">
                      Token spend saved (${annualSavingsLow.toLocaleString()}–${annualSavingsHigh.toLocaleString()})
                    </div>
                  </div>
                </div>

                <Button variant="primary" block onClick={() => contactFrom('calculator', calcSeats)}>
                  Request a pilot for your team →
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ────────────────────────────────── 5. Pilot & Contact Form ─────── */}
        <section className="shell" id="contact" ref={contactFormRef} style={{ paddingBottom: 'var(--s8)', scrollMarginTop: '4rem' }}>
          <div className="grid-2" style={{ gap: 'var(--s6)', alignItems: 'flex-start' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                <Label index="05">Enterprise Contact</Label>
                <h2>Start a 14-day pilot on your own repositories.</h2>
                <p className="dim" style={{ margin: 0 }}>
                  A pilot is a governed rollout of the agents your engineers already want, on your own repositories. Meet
                  the founding engineering team to arrange the security review, connect your identity provider, and agree
                  what the pilot measures.
                </p>
              </div>

              <div className="glass stack" style={{ ['--gap' as string]: 'var(--s3)', padding: 'var(--s5)' }}>
                <h4 style={{ margin: 0, fontFamily: 'var(--display)' }}>What happens next:</h4>
                <ol className="stack mono-note" style={{ ['--gap' as string]: 'var(--s2)', paddingLeft: '1.2rem', margin: 0 }}>
                  <li><strong>30-Minute Architecture Review:</strong> Your coding agents, your repositories, and your identity requirements, directly with the founders.</li>
                  <li><strong>14-day pilot:</strong> A group of your engineers work with and without Archis, and the pilot measures the tokens of each session. Reporting pull requests returned for rework, reverts and review minutes is in development.</li>
                  <li><strong>With several repositories:</strong> A second track on your CAVS DB Server: register the repositories, compose a workspace, lock it on two machines, and replay an incident.</li>
                  <li><strong>Security &amp; Procurement Package:</strong> Our data processing agreement on request (in counsel review), security questionnaire answers, and annual invoice onboarding.</li>
                </ol>
              </div>

              <div className="row mono-note" style={{ gap: 'var(--s3)' }}>
                <span>Questions? Direct inquiries to:</span>
                <a href="mailto:support@archis.dev" className="signal" onClick={() => trackCta('email', 'contact')}>support@archis.dev</a>
              </div>
            </div>

            {/* Form Card */}
            <div className="card stack" style={{ padding: 'var(--s6)' }}>
              {enterpriseSubmitted ? (
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)', padding: 'var(--s4) 0' }}>
                  <div className="row" style={{ gap: 'var(--s2)', color: 'var(--signal)', alignItems: 'center' }}>
                    <CheckIcon className="signal" style={{ width: 24, height: 24 }} />
                    <h3 style={{ margin: 0 }}>Inquiry Received Successfully</h3>
                  </div>
                  <p className="dim">
                    Thank you for reaching out, <strong>{inquiryName}</strong>. Our founding engineering team has received your request for <strong>{inquiryCompany || 'your organization'}</strong> and will follow up at <strong>{inquiryEmail}</strong> within 1 business day.
                  </p>
                  <Button variant="default" onClick={() => setEnterpriseSubmitted(false)}>
                    Submit Another Inquiry
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <h3 style={{ margin: 0, fontFamily: 'var(--display)' }}>Request a pilot</h3>
                  <p className="dim" style={{ margin: 0, fontSize: '0.875rem' }}>
                    Provide your details to connect directly with the founders:
                  </p>

                  <div className="grid-2" style={{ gap: 'var(--s3)' }}>
                    <Field label="Your Full Name">
                      <input
                        className="input"
                        type="text"
                        required
                        placeholder="Grace Hopper"
                        value={inquiryName}
                        onChange={(e) => setInquiryName(e.target.value)}
                      />
                    </Field>

                    <Field label="Work Email">
                      <input
                        className="input"
                        type="email"
                        required
                        placeholder="grace@company.com"
                        value={inquiryEmail}
                        onChange={(e) => setInquiryEmail(e.target.value)}
                      />
                    </Field>
                  </div>

                  <div className="grid-2" style={{ gap: 'var(--s3)' }}>
                    <Field label="Company / Organization">
                      <input
                        className="input"
                        type="text"
                        required
                        placeholder="Acme Corporation"
                        value={inquiryCompany}
                        onChange={(e) => setInquiryCompany(e.target.value)}
                      />
                    </Field>

                    <Field label="Engineering Team Size">
                      <select
                        className="input"
                        value={inquiryTeamSize}
                        onChange={(e) => setInquiryTeamSize(e.target.value)}
                        required
                      >
                        <option value="" disabled>
                          Select…
                        </option>
                        <option value="1-19">Under 20 developers</option>
                        <option value="20-50">20–50 developers</option>
                        <option value="50-200">50–200 developers</option>
                        <option value="200-1000">200–1,000 developers</option>
                        <option value="1000+">1,000+ developers</option>
                      </select>
                    </Field>
                  </div>

                  <Field label="Identity provider (optional)">
                    <select
                      className="input"
                      value={inquiryIdp}
                      onChange={(e) => setInquiryIdp(e.target.value)}
                    >
                      <option value="">Not decided yet</option>
                      <option value="okta">Okta (SAML 2.0 / SCIM)</option>
                      <option value="azure-ad">Microsoft Entra ID (Azure AD)</option>
                      <option value="google-workspace">Google Workspace</option>
                      <option value="ping-identity">Ping Identity</option>
                      <option value="other">Other / Custom SAML</option>
                    </select>
                  </Field>

                  <Field label="Specific Requirements or Monorepo Scale (Optional)">
                    <textarea
                      className="input"
                      rows={3}
                      placeholder="e.g. 500k+ LOC monorepo, Okta SSO, DPA review / invoicing..."
                      value={inquiryNotes}
                      onChange={(e) => setInquiryNotes(e.target.value)}
                    />
                  </Field>

                  {/* Anti-bot Honeypot field (invisible to humans) */}
                  <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
                    <label htmlFor="ent-website">Website</label>
                    <input
                      id="ent-website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>

                  {/* Cloudflare Turnstile Bot Protection */}
                  <Turnstile onVerify={setCfToken} onExpire={() => setCfToken('')} />

                  <ErrorNotice error={enterpriseLead.error} />

                  <Button
                    type="submit"
                    variant="primary"
                    block
                    loading={enterpriseLead.isPending}
                  >
                    Request my pilot →
                  </Button>

                  <p className="mono-note center" style={{ margin: 0, fontSize: '0.75rem' }}>
                    Archis receives no code during a pilot · A founder replies within one business day
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* ────────────────────────────────── 6. Enterprise FAQ ───────────── */}
        <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
              <Label index="06">Due Diligence FAQ</Label>
              <h2>Frequently asked enterprise &amp; InfoSec questions.</h2>
            </div>

            <div className="faq">
              {[
                {
                  q: 'Where is our source code stored and processed?',
                  a: 'Nowhere on Archis infrastructure. All source code parsing, Tree-sitter AST compilation, and SQLite knowledge graph queries execute strictly on the developer’s local laptop or within your private CI runners. Archis cloud servers only handle licence verification and team administration.',
                },
                {
                  q: 'Do you use our source code to train AI models?',
                  a: 'Never. Archis is a local semantic context compiler, not an LLM vendor. There is literally no backend endpoint designed to receive source code or AST tokens.',
                },
                {
                  q: 'Does Archis handle large monorepos?',
                  a: 'Indexing runs on each machine: angular’s 10,513 files index in about 18 seconds on one laptop, and afterwards only changed files are re-indexed. A shared, pre-built index for very large monorepos is not shipped, and the CAVS DB Server does not provide one: it holds ledgers and compositions, so it scopes an agent to the repositories a change touches rather than shortening the first index. Tell us your repository size and we will say where it stands.',
                },
                {
                  q: 'What happens if an employee is terminated or a laptop is lost?',
                  a: 'Deactivating someone in your identity provider reaches Archis over SCIM 2.0: their seat is released and their devices and sign-in tokens are revoked. An admin can also revoke a device from the portal. A laptop that stays offline keeps its current lease until it runs out, at most four days, or less if you shorten the workspace’s offline grace. An offline licence file cannot be revoked and runs until it expires. On your CAVS DB Server, a sign-in lasts seven days and Archis cannot revoke it: a removed member can keep reading what the server holds until it expires, unless you run cavsdb-server user block, which refuses them at once. What their machine already fetched stays on it, like a clone.',
                },
                {
                  q: 'Can Archis operate in air-gapped, VPN, or zero-connectivity environments?',
                  a: 'Offline, yes: a signed lease keeps licensed commands working for up to four days without a connection (valid for 24 hours, then a 72-hour grace), and local data is never deleted when it runs out. For networks that never connect, Enterprise issues an offline licence file, signed for the machine and valid for a set period.',
                },
                {
                  q: 'Does the CAVS DB Server see our source code?',
                  a: 'It is your server, on your infrastructure, and nothing on it reaches Archis. It always holds each repository’s ledger (paths, hashes, commits) and the compositions over them. Whether file content goes with it is set per repository: in metadata mode a member without a checkout lists paths and never reads bytes; hybrid, the default, publishes text files up to 1 MiB and full publishes every file, so members without a checkout can search them.',
                },
                {
                  q: 'What happens if our licence or subscription lapses?',
                  a: 'Nothing on your machines is deleted. The account drops to Free, local indexes stay, and the commands that clean up federation still run. A CAVS DB Server whose licence expires or is revoked turns read-only: reads, compositions and locks keep working, and publishing stops until it is renewed.',
                },
                {
                  q: 'How does enterprise invoicing and procurement work?',
                  // claims-ok(sla-msa): says that no SLA exists
                  a: 'Annual contract billing with corporate invoice terms (Net 30, ACH or wire transfer). Our data processing agreement is in counsel review, and we send the current draft on request. There is no response-time SLA yet.',
                },
              ].map((item) => (
                <details className="faq__item" key={item.q}>
                  <summary>{item.q}</summary>
                  <div className="faq__answer">
                    <p>{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ────────────────────────────────── 7. Final CTA ────────────────── */}
        <section className="shell" style={{ paddingBottom: 'var(--s9)' }}>
          <div className="panel panel--signal center" style={{ padding: 'var(--s7) var(--s5)' }}>
            <h2 style={{ maxWidth: '32ch', margin: '0 auto' }}>
              Roll out coding agents on your company&apos;s code with the controls already in place.
            </h2>
            <p className="dim" style={{ maxWidth: '56ch', margin: 'var(--s4) auto var(--s5)' }}>
              Start with a pilot on your own repositories. Today it measures tokens per session; rework and review time are in development.
            </p>
            <div className="row" style={{ justifyContent: 'center', gap: 'var(--s3)', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="cta cta--primary"
                onClick={() => contactFrom('closing')}
              >
                Request a pilot ↓
              </button>
              <Link to="/pricing" className="cta cta--secondary" onClick={() => trackCta('pricing', 'closing')}>
                View All Pricing Tiers →
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
